import React, { useState, useEffect } from 'react';
import { FiShoppingCart, FiTrash2, FiPlus, FiMinus, FiX, FiTag, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { cartService, CartItem } from '../services/cartService';
import { apiUrls } from '../config/api';

interface CartDisplayProps {
  isOpen: boolean;
  onClose: () => void;
  onContinueShopping?: () => void;
  isAuthenticated?: boolean;
  onLoginClick?: () => void;
}

interface CouponValidation {
  valid: boolean;
  coupon?: {
    code: string;
    name: string;
    discount_type: string;
    discount_value: number;
  };
  discount_details?: {
    original_amount: number;
    discount_amount: number;
    final_amount: number;
  };
  reason?: string;
}

const CartDisplay: React.FC<CartDisplayProps> = ({ isOpen, onClose, onContinueShopping, isAuthenticated = false, onLoginClick }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [total, setTotal] = useState(0);
  
  // Coupon states
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidation | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);

  // Safe price formatting function
  const formatPrice = (price: any): string => {
    try {
      const numPrice = Number(price);
      if (isNaN(numPrice)) return '0.00';
      return numPrice.toFixed(2);
    } catch (error) {
      console.error('Error formatting price:', price, error);
      return '0.00';
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadCartData();
    }

    const handleCartUpdate = () => {
      loadCartData();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, [isOpen]);

  const loadCartData = () => {
    const items = cartService.getCartItems();
    const totalAmount = cartService.getCartTotal();
    setCartItems(items);
    setTotal(totalAmount);
  };

  const updateQuantity = (id: number, type: 'book' | 'package' | 'course', quantity: number) => {
    cartService.updateQuantity(id, type, quantity);
    loadCartData();
  };

  const removeItem = (id: number, type: 'book' | 'package' | 'course') => {
    cartService.removeFromCart(id, type);
    loadCartData();
  };

  const clearCart = () => {
    cartService.clearCart();
    loadCartData();
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      setCouponLoading(true);
      setCouponError(null);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id || 1;

      const response = await fetch(apiUrls.coupons.validate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.toUpperCase(),
          user_id: userId,
          purchase_amount: total
        })
      });

      const data = await response.json();

      if (data.success && data.valid) {
        setAppliedCoupon(data);
        setCouponError(null);
      } else {
        setCouponError(data.reason || data.error || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (err: any) {
      setCouponError('Failed to validate coupon. Please try again.');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  const getFinalTotal = () => {
    if (appliedCoupon && appliedCoupon.discount_details) {
      return appliedCoupon.discount_details.final_amount;
    }
    return total;
  };

  const getDiscount = () => {
    if (appliedCoupon && appliedCoupon.discount_details) {
      return appliedCoupon.discount_details.discount_amount;
    }
    return 0;
  };

  if (!isOpen) return null;

  console.log('CartDisplay rendering with isOpen:', isOpen);
  console.log('Cart items:', cartItems);
  console.log('Cart total:', total);
  
  // Debug price values
  cartItems.forEach((item, index) => {
    console.log(`Item ${index}:`, {
      id: item.id,
      title: item.title,
      price: item.price,
      priceType: typeof item.price,
      quantity: item.quantity
    });
  });

  // Add custom scrollbar styles
  const scrollbarStyles = `
    <style>
      .cart-items-scroll::-webkit-scrollbar {
        width: 8px;
      }
      .cart-items-scroll::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .cart-items-scroll::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }
      .cart-items-scroll::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    </style>
  `;

  // Add error boundary for cart rendering
  try {
    return (
    <>
    <div dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Debug indicator */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '650px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiShoppingCart size={20} />
            <h3 style={{ margin: 0, color: '#1f2937' }}>Shopping Cart</h3>
            <span style={{
              background: '#3b82f6',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {cartItems.reduce((count, item) => count + item.quantity, 0)}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6b7280'
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Cart Items */}
        <div 
          className="cart-items-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '20px',
            maxHeight: '400px',
            minHeight: '200px'
          }}
        >
          {cartItems.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6b7280'
            }}>
              <FiShoppingCart size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <h4 style={{ margin: '0 0 8px 0' }}>Your cart is empty</h4>
              <p style={{ margin: '0 0 20px 0' }}>Add some books, packages, or courses to get started!</p>
              <button
                onClick={() => {
                  if (onContinueShopping) {
                    onContinueShopping();
                  }
                  onClose();
                }}
                style={{
                  background: 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(37, 60, 140, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(37, 60, 140, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(37, 60, 140, 0.2)';
                }}
              >
                <FiShoppingCart size={16} />
                Browse Books & Courses
              </button>
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '16px',
              paddingRight: '8px'
            }}>
              {cartItems.map((item) => (
                <div
                  key={`${item.id}-${item.type}`}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: '#f9fafb',
                    transition: 'all 0.2s ease',
                    cursor: 'default'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {/* Item Image */}
                  <img
                    src={item.image || '/mathknots-logo.png'}
                    alt={item.title}
                    style={{
                      width: '60px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                  
                  {/* Item Details */}
                  <div style={{ flex: 1 }}>
                    <h5 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                      {item.title}
                    </h5>
                    <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                      {item.type === 'book' ? '📚 Book' : '📦 Package'}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={() => updateQuantity(item.id, item.type, item.quantity - 1)}
                        style={{
                          background: '#e5e7eb',
                          border: 'none',
                          borderRadius: '4px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <FiMinus size={14} />
                      </button>
                      
                      <span style={{
                        minWidth: '20px',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}>
                        {item.quantity}
                      </span>
                      
                      <button
                        onClick={() => updateQuantity(item.id, item.type, item.quantity + 1)}
                        style={{
                          background: '#e5e7eb',
                          border: 'none',
                          borderRadius: '4px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <FiPlus size={14} />
                      </button>
                      
                      <button
                        onClick={() => removeItem(item.id, item.type)}
                        style={{
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Item Price */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ color: '#1f2937', fontWeight: 'bold', fontSize: '16px' }}>
                      ${formatPrice(Number(item.price || 0) * item.quantity)}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      ${formatPrice(item.price || 0)} each
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #e5e7eb',
            background: '#f9fafb'
          }}>
            {/* Coupon Section */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '8px' 
              }}>
                <FiTag style={{ display: 'inline', marginRight: '6px' }} />
                Have a coupon code?
              </label>
              
              {!appliedCoupon ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Enter coupon code"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: `1px solid ${couponError ? '#ef4444' : '#d1d5db'}`,
                      borderRadius: '6px',
                      fontSize: '14px',
                      textTransform: 'uppercase'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') validateCoupon();
                    }}
                  />
                  <button
                    onClick={validateCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    style={{
                      padding: '10px 20px',
                      background: couponLoading ? '#9ca3af' : '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: couponLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {couponLoading ? 'Checking...' : 'Apply'}
                  </button>
                </div>
              ) : (
                <div style={{
                  padding: '12px',
                  background: '#d1fae5',
                  border: '1px solid #10b981',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FiCheck style={{ color: '#065f46' }} />
                    <span style={{ color: '#065f46', fontWeight: '600', fontSize: '14px' }}>
                      {appliedCoupon.coupon?.code} Applied
                    </span>
                    {appliedCoupon.coupon?.name && (
                      <span style={{ color: '#059669', fontSize: '12px' }}>
                        ({appliedCoupon.coupon.name})
                      </span>
                    )}
                  </div>
                  <button
                    onClick={removeCoupon}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#065f46',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Remove coupon"
                  >
                    <FiX size={18} />
                  </button>
                </div>
              )}
              
              {couponError && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FiAlertCircle size={14} />
                  {couponError}
                </div>
              )}
            </div>

            {/* Price Summary */}
            <div style={{
              marginBottom: '16px',
              padding: '12px 0',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '8px',
                color: '#6b7280',
                fontSize: '14px'
              }}>
                <span>Subtotal:</span>
                <span>${formatPrice(total || 0)}</span>
              </div>
              
              {appliedCoupon && getDiscount() > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  color: '#10b981',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  <span>
                    Discount ({appliedCoupon.coupon?.discount_type === 'percentage' 
                      ? `${appliedCoupon.coupon?.discount_value}%` 
                      : `$${appliedCoupon.coupon?.discount_value}`}):
                  </span>
                  <span>-${formatPrice(getDiscount())}</span>
                </div>
              )}
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: '12px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1f2937'
              }}>
                <span>Total:</span>
                <span>${formatPrice(getFinalTotal())}</span>
              </div>
              
              {appliedCoupon && getDiscount() > 0 && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#10b981',
                  textAlign: 'right'
                }}>
                  You save ${formatPrice(getDiscount())}!
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <button
                onClick={clearCart}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Clear Cart
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  if (onContinueShopping) {
                    onContinueShopping();
                  }
                  onClose();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e5e7eb';
                }}
              >
                <FiShoppingCart size={16} />
                Continue Shopping
              </button>
              <button
                onClick={() => {
                  // Check if user is authenticated
                  if (!isAuthenticated) {
                    // Close cart and redirect to login
                    onClose();
                    if (onLoginClick) {
                      onLoginClick();
                    } else {
                      alert('Please login to proceed to checkout');
                    }
                    return;
                  }
                  
                  console.log('🛒 Checkout button clicked!');
                  console.log('Cart items:', cartItems);
                  console.log('Applied coupon:', appliedCoupon);
                  
                  // Save coupon data to session storage for checkout page
                  if (appliedCoupon) {
                    sessionStorage.setItem('appliedCoupon', JSON.stringify({
                      code: appliedCoupon.coupon?.code,
                      name: appliedCoupon.coupon?.name,
                      discount_type: appliedCoupon.coupon?.discount_type,
                      discount_value: appliedCoupon.coupon?.discount_value,
                      discount_amount: getDiscount(),
                      finalTotal: getFinalTotal()
                    }));
                  }
                  
                  console.log('Navigating to checkout...');
                  // Navigate to checkout page
                  window.location.href = '/?page=checkout';
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: isAuthenticated ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isAuthenticated ? 'pointer' : 'not-allowed',
                  fontWeight: '500',
                  opacity: isAuthenticated ? 1 : 0.7
                }}
                title={!isAuthenticated ? 'Please login to checkout' : ''}
              >
                {isAuthenticated ? (
                  <>Checkout {appliedCoupon && `• Save $${formatPrice(getDiscount())}`}</>
                ) : (
                  <>🔒 Login to Checkout</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
    );
  } catch (error) {
    console.error('Error rendering cart:', error);
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '400px',
            padding: '20px',
            textAlign: 'center'
          }}
        >
          <h3 style={{ color: '#ef4444', marginBottom: '16px' }}>Cart Error</h3>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>
            There was an error loading your cart. Please try again.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }
};

export default CartDisplay;
